/**
 * Utility functions for date handling with Buddhist Era (พ.ศ.) support
 */

/**
 * Convert Christian Era (CE) year to Buddhist Era (BE)
 * @param ceYear - Year in CE
 * @returns Year in BE
 */
export const ceToBe = (ceYear: number): number => {
  return ceYear + 543;
};

/**
 * Convert Buddhist Era (BE) year to Christian Era (CE)
 * @param beYear - Year in BE
 * @returns Year in CE
 */
export const beToCe = (beYear: number): number => {
  return beYear - 543;
};

/**
 * Convert date string from CE to BE format for display
 * @param dateString - Date string in ISO format (YYYY-MM-DD)
 * @returns Date string in BE format (YYYY-MM-DD where YYYY is in BE)
 */
export const dateStringCeToBe = (dateString: string): string => {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("-");
  const beYear = ceToBe(parseInt(year, 10));
  return `${beYear}-${month}-${day}`;
};

/**
 * Convert date string from BE to CE format for API
 * @param dateString - Date string in BE format (YYYY-MM-DD where YYYY is in BE)
 * @returns Date string in CE format (YYYY-MM-DD)
 */
export const dateStringBeToCe = (dateString: string): string => {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("-");
  const ceYear = beToCe(parseInt(year, 10));
  return `${ceYear}-${month}-${day}`;
};

/**
 * Format date object to Thai format with Buddhist Era
 * @param date - Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string in Thai with BE year
 */
export const formatDateThai = (
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  }
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Format with Thai locale
  const formatted = new Intl.DateTimeFormat("th-TH", {
    ...options,
    calendar: "buddhist",
  }).format(dateObj);

  return formatted;
};

/**
 * Get current date in BE format for input[type="date"]
 * @returns Date string in YYYY-MM-DD format with BE year
 */
export const getTodayInBE = (): string => {
  const today = new Date();
  const year = ceToBe(today.getFullYear());
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Get min/max date for input validation in BE format
 * @param daysOffset - Number of days to offset from today (positive for future, negative for past)
 * @returns Date string in YYYY-MM-DD format with BE year
 */
export const getOffsetDateInBE = (daysOffset: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);

  const year = ceToBe(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parse date string in DD/MM/YYYY format (where YYYY is in BE)
 * and convert to Date object in CE
 * @param dateString - Date string in DD/MM/YYYY format with BE year
 * @returns Date object in CE or null if invalid
 */
export const parseDateFromBE = (dateString: string): Date | null => {
  if (!dateString) return null;

  // Handle DD/MM/YYYY format
  const parts = dateString.split("/");
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const yearBE = parseInt(parts[2], 10);

    // Convert BE year to CE
    const yearCE = beToCe(yearBE);

    // Create date object
    const date = new Date(yearCE, month, day);

    // Validate the date
    if (
      date.getFullYear() === yearCE &&
      date.getMonth() === month &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  return null;
};

/**
 * Convert Date object to CE ISO string (YYYY-MM-DD)
 * @param date - Date object in CE
 * @returns ISO date string without time
 */
export const formatDateToCeIso = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parse CE ISO string (YYYY-MM-DD) into Date object
 * @param dateString - CE ISO string
 * @returns Date object or null if invalid
 */
export const parseCeIsoDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const trimmed = dateString.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);

  const date = new Date(year, month, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

/**
 * Format Date object to DD/MM/YYYY string with BE year
 * @param date - Date object in CE
 * @returns Formatted date string in DD/MM/YYYY with BE year
 */
export const formatDateToBEDisplay = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const yearBE = ceToBe(date.getFullYear());
  return `${day}/${month}/${yearBE}`;
};

/**
 * Convert CE ISO string to DD/MM/YYYY format with BE year
 * @param dateString - CE ISO string (YYYY-MM-DD)
 * @returns Formatted date string or empty string if invalid
 */
export const formatCeIsoToBEDisplay = (dateString: string): string => {
  const date = parseCeIsoDate(dateString);
  if (!date) return "";
  return formatDateToBEDisplay(date);
};

/**
 * Parse DD/MM/YYYY string with BE year to CE ISO string
 * @param input - Date string in DD/MM/YYYY (BE)
 * @returns CE ISO string (YYYY-MM-DD) or null if invalid
 */
export const parseBeInputToCeIso = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return "";

  const parsedDate = parseDateFromBE(trimmed);
  if (!parsedDate) return null;

  return formatDateToCeIso(parsedDate);
};
